/**
 * /app/growth-campaigns — Admin Growth Campaigns (SCRUM-223/224).
 * Distinto de /app/campaigns (email blast).
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Megaphone, Plus, Rocket } from "lucide-react";
import { CrudShellHeader } from "@/components/ds/CrudShell";
import { ClinicalPageShell } from "@/components/clinical";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import type { GrowthCampaign, GrowthCampaignTrigger } from "@/lib/growth-campaigns/types";

export const Route = createFileRoute("/app/growth-campaigns")({
  component: GrowthCampaignsPage,
  head: () => ({ meta: [{ title: "Growth Campaigns — Structura Market" }] }),
});

type TriggerType = GrowthCampaignTrigger["type"];

function triggerType(t: GrowthCampaignTrigger): TriggerType {
  return t.type;
}

function GrowthCampaignsPage() {
  const tenantId = useWorkspaceTenantId();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lock, setLock] = useState<PlanApiErrorPayload | null>(null);
  const [items, setItems] = useState<GrowthCampaign[]>([]);
  const [name, setName] = useState("Welcome");
  const [headline, setHeadline] = useState("¡Bienvenido!");
  const [trigger, setTrigger] = useState<TriggerType>("welcome");
  const [couponCode, setCouponCode] = useState("");
  const [maxImpressions, setMaxImpressions] = useState(3);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLock(null);
    try {
      const res = await apiGetJson<{ items: GrowthCampaign[] }>(
        "/api/growth-campaigns",
        tenantId,
      );
      setItems(res.items ?? []);
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error(err instanceof Error ? err.message : "No se pudieron cargar campañas");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createDraft = async () => {
    if (!tenantId) return;
    setSaving(true);
    try {
      const triggerBody: GrowthCampaignTrigger =
        trigger === "page_view"
          ? { type: "page_view" }
          : trigger === "custom"
            ? { type: "custom", key: "custom" }
            : { type: trigger };
      await apiSendJson(
        "/api/growth-campaigns",
        "POST",
        {
          name: name.trim() || "Campaña",
          headline: headline.trim() || undefined,
          status: "draft",
          trigger: triggerBody,
          surface: "campaign",
          frequency: { maxImpressionsPerUser: maxImpressions, oncePerSession: true },
          incentive: couponCode.trim()
            ? { kind: "coupon", couponCode: couponCode.trim().toUpperCase() }
            : { kind: "none" },
          attribution: { channel: "growth", utmCampaign: name.trim() || "growth" },
        },
        tenantId,
      );
      toast.success("Campaña creada en borrador");
      await load();
    } catch (e: unknown) {
      const err = e as Error & { planLock?: PlanApiErrorPayload };
      if (err.planLock) setLock(err.planLock);
      else toast.error(err instanceof Error ? err.message : "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const publish = async (id: string) => {
    if (!tenantId) return;
    try {
      await apiSendJson(
        "/api/growth-campaigns",
        "PATCH",
        { id, action: "publish" },
        tenantId,
      );
      toast.success("Campaña publicada (active)");
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "No se pudo publicar");
    }
  };

  const setStatus = async (id: string, status: GrowthCampaign["status"]) => {
    if (!tenantId) return;
    const existing = items.find((i) => i.id === id);
    if (!existing) return;
    try {
      await apiSendJson(
        "/api/growth-campaigns",
        "PATCH",
        {
          id,
          status,
          trigger: existing.trigger,
          surface: existing.surface,
          audience: existing.audience,
          schedule: existing.schedule,
          frequency: existing.frequency,
          incentive: existing.incentive,
          attribution: existing.attribution,
          name: existing.name,
        },
        tenantId,
      );
      toast.success(`Estado → ${status}`);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al actualizar");
    }
  };

  if (lock) return <FeatureLockedState payload={lock} />;

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando Growth Campaigns…
      </div>
    );
  }

  return (
    <div>
      <CrudShellHeader
        title="Growth Campaigns"
        description="Welcome, ruleta, carrito abandonado y overlays — capa reutilizable (no es email blast)"
      />
      <ClinicalPageShell offset="none" className="space-y-6 pt-6">
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Megaphone className="h-4 w-4" /> Nueva campaña
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Nombre</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Headline</Label>
              <Input value={headline} onChange={(e) => setHeadline(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Trigger</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
                value={trigger}
                onChange={(e) => setTrigger(e.target.value as TriggerType)}
              >
                <option value="welcome">Welcome</option>
                <option value="wheel">Ruleta</option>
                <option value="abandoned_cart">Carrito abandonado</option>
                <option value="page_view">Page view</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Cupón (opcional)</Label>
              <Input
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="WELCOME10"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Máx. impresiones / usuario</Label>
              <Input
                type="number"
                min={1}
                value={maxImpressions}
                onChange={(e) => setMaxImpressions(Number(e.target.value) || 1)}
              />
            </div>
          </div>
          <Button onClick={() => void createDraft()} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Crear borrador
          </Button>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">Campañas ({items.length})</p>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Todavía no hay campañas. Creá un Welcome para empezar.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/30 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Trigger</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Incentivo</th>
                    <th className="px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((c) => (
                    <tr key={c.id} className="bg-card">
                      <td className="px-3 py-2 font-medium">{c.name || c.id.slice(0, 8)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{triggerType(c.trigger)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{c.status}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {c.incentive?.couponCode
                          ? `Cupón ${c.incentive.couponCode}`
                          : c.incentive?.kind ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {c.status !== "active" ? (
                            <Button size="sm" variant="secondary" className="h-7 gap-1" onClick={() => void publish(c.id)}>
                              <Rocket className="h-3 w-3" /> Publicar
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7" onClick={() => void setStatus(c.id, "paused")}>
                              Pausar
                            </Button>
                          )}
                          {c.status === "paused" ? (
                            <Button size="sm" variant="outline" className="h-7" onClick={() => void setStatus(c.id, "active")}>
                              Reactivar
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </ClinicalPageShell>
    </div>
  );
}
